import React from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import { BulkActionBar } from './BulkActionBar';

export interface UploadFileListProps {
  totalCount: number;
  selectedCount: number;
  /** Master-checkbox state. */
  allSelected: boolean;
  someSelected: boolean;
  onToggleAll: () => void;
  /** Bulk action handlers — only invoked when selectedCount > 0. */
  onBulkSetStatus: (anchor: HTMLElement) => void;
  onBulkAddTags: (anchor: HTMLElement) => void;
  onBulkRemove: () => void;
  onBulkClear: () => void;
  /** Rendered rows (UploadFileRow instances) — children. */
  children: React.ReactNode;
}

export const UploadFileList: React.FC<UploadFileListProps> = ({
  totalCount,
  selectedCount,
  allSelected,
  someSelected,
  onToggleAll,
  onBulkSetStatus,
  onBulkAddTags,
  onBulkRemove,
  onBulkClear,
  children,
}) => (
  <Box
    sx={(theme) => ({
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
      background: theme.palette.background.paper,
    })}
  >
    <Box
      sx={(theme) => ({
        position: 'sticky',
        top: 0,
        zIndex: 3,
        background: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        px: 2.25,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Checkbox
          size="small"
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          onChange={onToggleAll}
          sx={{ p: 0.5 }}
          inputProps={{ 'aria-label': 'Select all files' }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          {totalCount} {totalCount === 1 ? 'file' : 'files'}
        </Typography>
      </Box>
      {selectedCount === 0 ? (
        <Typography variant="caption" color="text.disabled">
          Click ☐ to multi-select for bulk edits
        </Typography>
      ) : (
        <BulkActionBar
          selectedCount={selectedCount}
          onSetStatus={onBulkSetStatus}
          onAddTags={onBulkAddTags}
          onRemove={onBulkRemove}
          onClear={onBulkClear}
        />
      )}
    </Box>
    {children}
  </Box>
);
